import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, TextField, MenuItem, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AdjustIcon from '@mui/icons-material/Adjust';
import {
    useDeficiencies,
    getAssignmentMetadata,
    ACTIONS,
} from '../../../Pages/Deficiencies/DeficiencyContext';
import { DEFICIENCY_STATUSES, getDisplayStatusValue } from '../../../Pages/Deficiencies/constants';
import { formatDate } from '../../../utils/dateUtils';
import theme from '../../../../theme';
import { getTrackerStatuses, getStatusDisplay } from '../../../../config/statusDisplayResolver';
import { isInternalManagerRole } from '../../../../utils/roles';

// Define the status flow based on the official statuses
const STATUSES = [
    DEFICIENCY_STATUSES.SUBMITTED,
    DEFICIENCY_STATUSES.ACCEPTED,
    DEFICIENCY_STATUSES.ASSIGNED,
    DEFICIENCY_STATUSES.WORK_COMPLETED,
    DEFICIENCY_STATUSES.INTERNALLY_SIGNED_OFF,
    DEFICIENCY_STATUSES.COMPLETED,
    DEFICIENCY_STATUSES.REJECTED,
];

// Component for the constant metadata section
const ConstantMetadata = ({
    deficiency,
    isEditing,
    onChange,
    businesses,
    businessesLoading,
    users,
    usersLoading,
    selectedStatus,
    readOnly = false,
    role,
}) => {
    // Handle field change
    const handleChange = (field) => (event) => {
        if (onChange) {
            onChange({
                ...deficiency,
                [field]: event.target.value,
            });
        }
    };

    // Handle status change
    const handleStatusChange = (event) => {
        if (!onChange) return;

        const newStatus = event.target.value;

        // Status metadata is now handled by the backend
        // Simply update the status field
        onChange({
            ...deficiency,
            status: newStatus,
        });
    };

    // For handling assignee selection with businessId
    const handleAssigneeChange = (event) => {
        const selectedBusinessId = event.target.value;

        if (!onChange) return;

        // Find the selected business from the businesses array
        const selectedBusiness = businesses?.find(
            (business) => business.businessId === selectedBusinessId
        );

        // Use the centralized helper function to get assignment metadata
        const assignmentMetadata = getAssignmentMetadata(
            selectedBusinessId,
            selectedBusiness,
            deficiency.status,
            null // editorId not needed for this context
        );

        // Apply assignment metadata to deficiency
        onChange({
            ...deficiency,
            ...assignmentMetadata,
        });
    };

    // Common field styles - enhanced to look more like plain text with subtle interaction cues
    const fieldStyles = {
        mt: 0.5,
        '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            fontSize: '0.75rem',
            backgroundColor: 'transparent',
            '& fieldset': {
                borderColor: 'transparent',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(22, 82, 240, 0.2)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1652F0',
            },
        },
        '& .MuiOutlinedInput-input': {
            py: 0.25,
            px: 0.5,
            color: '#1E2026',
            fontWeight: 500,
        },
        '& .MuiSelect-select': {
            padding: '2px 4px',
            fontWeight: 500,
            color: '#1E2026',
            whiteSpace: 'normal',
            lineHeight: 1.2,
            minHeight: '1.5rem',
            wordBreak: 'break-word',
        },
        '& .MuiInputBase-input': {
            color: '#1E2026',
            fontWeight: 500,
        },
    };

    // Special styles just for assignee dropdowns to ensure wrapping works
    const assigneeFieldStyles = {
        ...fieldStyles,
        '& .MuiSelect-select': {
            whiteSpace: 'normal !important',
            textOverflow: 'unset',
            overflow: 'visible',
            height: 'auto',
            minHeight: '1.5rem',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            width: '100%',
            display: 'block',
            padding: '2px 4px',
            fontWeight: 500,
            color: '#1E2026',
        },
        '& .MuiSelect-icon': {
            color: '#1652F0',
            opacity: 0.6,
        },
    };

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: '#F8F9FA',
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {/* Created On */}
                <Box sx={{ width: 'calc(50% - 8px)' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                    >
                        Created On
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1E2026' }}>
                        {formatDate(deficiency.dateCreated)}
                    </Typography>
                </Box>

                {/* Created By */}
                <Box sx={{ width: 'calc(50% - 8px)' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                    >
                        Created By
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1E2026' }}>
                        {(() => {
                            if (usersLoading) {
                                return 'Loading...';
                            }

                            const creatorUser = users?.find(
                                (user) => user.userId === deficiency.createdById
                            );
                            if (creatorUser) {
                                return `${creatorUser.firstName} ${creatorUser.lastName}`;
                            }

                            return deficiency.createdById
                                ? `User ID: ${deficiency.createdById.substring(0, 8)}...`
                                : 'Unknown';
                        })()}
                    </Typography>
                </Box>

                {/* Current Status - Show as dropdown if editable, plain text if read-only */}
                <Box sx={{ width: 'calc(50% - 8px)' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                    >
                        Current Status
                    </Typography>
                    {readOnly ? (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1E2026' }}>
                            {role && !isInternalManagerRole(role)
                                ? getStatusDisplay(deficiency.status, role).displayLabel
                                : STATUSES.find((s) => s.value === deficiency.status)?.label ||
                                  deficiency.status ||
                                  'Unknown'}
                        </Typography>
                    ) : (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={deficiency.status || ''}
                            onChange={handleStatusChange}
                            sx={assigneeFieldStyles}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                    MenuListProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                lineHeight: 1.2,
                                                padding: '6px 10px',
                                            },
                                        },
                                    },
                                },
                                IconComponent: (props) => (
                                    <Box
                                        component="svg"
                                        {...props}
                                        sx={{ width: 14, height: 14, color: '#666', ml: -0.5 }}
                                    >
                                        <path d="M7 10l5 5 5-5z" />
                                    </Box>
                                ),
                            }}
                        >
                            {STATUSES.map((status) => (
                                <MenuItem key={status.value} value={status.value}>
                                    {status.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>

                {/* Assigned To - Show as dropdown if editable, plain text if read-only */}
                <Box sx={{ width: 'calc(50% - 8px)' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                    >
                        Assigned To
                    </Typography>
                    {businessesLoading ? (
                        <CircularProgress size={16} />
                    ) : readOnly ? (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1E2026' }}>
                            {deficiency.assignedToBusinessName || 'Unassigned'}
                        </Typography>
                    ) : (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={deficiency.assignedToBusinessId || ''}
                            onChange={handleAssigneeChange}
                            sx={assigneeFieldStyles}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        style: {
                                            maxHeight: 200,
                                        },
                                    },
                                    MenuListProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                lineHeight: 1.2,
                                                padding: '6px 10px',
                                            },
                                        },
                                    },
                                },
                                IconComponent: (props) => (
                                    <Box
                                        component="svg"
                                        {...props}
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            color: '#666',
                                            ml: -0.5,
                                        }}
                                    >
                                        <path d="M7 10l5 5 5-5z" />
                                    </Box>
                                ),
                            }}
                        >
                            <MenuItem value="">
                                <em>Unassigned</em>
                            </MenuItem>
                            {businesses &&
                                businesses.map((business) => (
                                    <MenuItem key={business.businessId} value={business.businessId}>
                                        {business.businessName}
                                    </MenuItem>
                                ))}
                        </TextField>
                    )}
                </Box>

                {/* Due Date - Show as date picker if editable, plain text if read-only */}
                <Box sx={{ width: 'calc(50% - 8px)' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                    >
                        Due Date
                    </Typography>
                    {readOnly ? (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1E2026' }}>
                            {deficiency.dueDate ? formatDate(deficiency.dueDate) : 'Not set'}
                        </Typography>
                    ) : (
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            value={deficiency.dueDate || ''}
                            onChange={handleChange('dueDate')}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                style: {
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: '#1E2026',
                                    padding: '2px 4px',
                                },
                            }}
                            sx={{
                                ...fieldStyles,
                                '& .MuiOutlinedInput-root': {
                                    ...fieldStyles['& .MuiOutlinedInput-root'],
                                    '&:hover': {
                                        cursor: 'pointer',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '2px 4px',
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// Component for the variable metadata section
const VariableMetadata = ({
    deficiency,
    selectedStatus,
    isEditing,
    onChange,
    businesses,
    businessesLoading,
    users,
    usersLoading,
    readOnly = false,
}) => {
    // Find the assigned business based on the businessId
    const assignedBusiness = businesses?.find(
        (business) => business.businessId === deficiency.assignedToBusinessId
    );

    // Note: For audit trail fields (createdBy, rejectedBy, etc.), we would need user data
    // which is separate from business data. These fields are currently not supported
    // TODO: Add separate user data fetching for audit trail fields if needed

    // Format business name if business exists, otherwise return a fallback message
    const formatBusinessName = (business, fallback = 'Not assigned') => {
        if (!business) return fallback;
        return business.businessName;
    };

    // Helper function to get user display name
    const getUserDisplayName = (userId, fallback = 'Not recorded') => {
        if (usersLoading) {
            return 'Loading...';
        }

        if (!userId) {
            return fallback;
        }

        const user = users?.find((u) => u.userId === userId);
        if (user) {
            return `${user.firstName} ${user.lastName}`;
        }

        return `User ID: ${userId.substring(0, 8)}...`;
    };

    // For handling assignee selection with businessId
    const handleAssigneeChange = (event) => {
        const selectedBusinessId = event.target.value;

        if (!onChange) return;

        // Find the selected business from the businesses array
        const selectedBusiness = businesses?.find(
            (business) => business.businessId === selectedBusinessId
        );

        // Use the centralized helper function to get assignment metadata
        const assignmentMetadata = getAssignmentMetadata(
            selectedBusinessId,
            selectedBusiness,
            deficiency.status,
            null // editorId not needed for this context
        );

        // Apply assignment metadata to deficiency
        onChange({
            ...deficiency,
            ...assignmentMetadata,
        });
    };

    // Handle field change for regular fields
    const handleChange = (field) => (event) => {
        if (onChange) {
            onChange({
                ...deficiency,
                [field]: event.target.value,
            });
        }
    };

    // Common field styles - enhanced to look more like plain text with subtle interaction cues
    const fieldStyles = {
        mt: 0.5,
        '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            fontSize: '0.75rem',
            backgroundColor: 'transparent',
            '& fieldset': {
                borderColor: 'transparent',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(22, 82, 240, 0.2)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1652F0',
            },
        },
        '& .MuiOutlinedInput-input': {
            py: 0.25,
            px: 0.5,
            color: '#1E2026',
            fontWeight: 500,
        },
        '& .MuiSelect-select': {
            padding: '2px 4px',
            fontWeight: 500,
            color: '#1E2026',
            whiteSpace: 'normal',
            lineHeight: 1.2,
            minHeight: '1.5rem',
            wordBreak: 'break-word',
        },
        '& .MuiInputBase-input': {
            color: '#1E2026',
            fontWeight: 500,
        },
    };

    // Special styles just for assignee dropdowns to ensure wrapping works
    const assigneeFieldStyles = {
        ...fieldStyles,
        '& .MuiSelect-select': {
            whiteSpace: 'normal !important',
            textOverflow: 'unset',
            overflow: 'visible',
            height: 'auto',
            minHeight: '1.5rem',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            width: '100%',
            display: 'block',
            padding: '2px 4px',
            fontWeight: 500,
            color: '#1E2026',
        },
        '& .MuiSelect-icon': {
            color: '#1652F0',
            opacity: 0.6,
        },
    };

    // Get the status that was active before current rejection (if rejected)
    const getRejectedFrom = () => {
        if (!deficiency.rejectedFromStatus) return 'Unknown';

        // Always try to convert from API value - the field comes from the backend
        return getDisplayStatusValue(deficiency.rejectedFromStatus);
    };

    // Get section title
    const getSectionTitle = () => {
        if (selectedStatus.value !== deficiency.status) {
            return `Status Details: ${selectedStatus.label}`;
        }
        return 'Current Status Details';
    };

    // Render metadata fields based on status
    const renderStatusMetadata = () => {
        const status = selectedStatus.value;

        switch (status) {
            case 'Submitted':
                return (
                    <>
                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Created By
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {(() => {
                                    if (usersLoading) {
                                        return 'Loading...';
                                    }

                                    const creatorUser = users?.find(
                                        (user) => user.userId === deficiency.createdById
                                    );
                                    if (creatorUser) {
                                        return `${creatorUser.firstName} ${creatorUser.lastName}`;
                                    }

                                    return deficiency.createdById
                                        ? `User ID: ${deficiency.createdById.substring(0, 8)}...`
                                        : 'Unknown';
                                })()}
                            </Typography>
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Submitted On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {formatDate(deficiency.dateCreated)}
                            </Typography>
                        </Box>
                    </>
                );

            case 'Accepted':
                return (
                    <>
                        <Box sx={{ width: 'calc(100% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Accepted On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {formatDate(deficiency.acceptedOn, 'Not recorded')}
                            </Typography>
                        </Box>
                    </>
                );

            case 'Assigned':
                return (
                    <>
                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Assigned To
                            </Typography>
                            {isEditing && !readOnly ? (
                                businessesLoading ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        value={deficiency.assignedToBusinessId || ''}
                                        onChange={handleAssigneeChange}
                                        sx={assigneeFieldStyles}
                                        SelectProps={{
                                            MenuProps: {
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 200,
                                                    },
                                                },
                                                MenuListProps: {
                                                    sx: {
                                                        '& .MuiMenuItem-root': {
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            lineHeight: 1.2,
                                                            padding: '6px 10px',
                                                        },
                                                    },
                                                },
                                            },
                                            IconComponent: (props) => (
                                                <Box
                                                    component="svg"
                                                    {...props}
                                                    sx={{
                                                        width: 14,
                                                        height: 14,
                                                        color: '#666',
                                                        ml: -0.5,
                                                    }}
                                                >
                                                    <path d="M7 10l5 5 5-5z" />
                                                </Box>
                                            ),
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Unassigned</em>
                                        </MenuItem>
                                        {businesses &&
                                            businesses.map((business) => (
                                                <MenuItem
                                                    key={business.businessId}
                                                    value={business.businessId}
                                                >
                                                    {business.businessName}
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                )
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                                >
                                    {formatBusinessName(assignedBusiness, 'Unassigned')}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Assigned On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {formatDate(deficiency.assignedOn, 'Not recorded')}
                            </Typography>
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Due Date
                            </Typography>
                            {readOnly ? (
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                                >
                                    {deficiency.dueDate
                                        ? formatDate(deficiency.dueDate)
                                        : 'Not set'}
                                </Typography>
                            ) : (
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    value={deficiency.dueDate || ''}
                                    onChange={handleChange('dueDate')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        style: {
                                            cursor: 'pointer',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            color: '#1E2026',
                                            padding: '2px 4px',
                                        },
                                    }}
                                    sx={{
                                        ...fieldStyles,
                                        '& .MuiOutlinedInput-root': {
                                            ...fieldStyles['& .MuiOutlinedInput-root'],
                                            '&:hover': {
                                                cursor: 'pointer',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '2px 4px',
                                        },
                                    }}
                                />
                            )}
                        </Box>
                    </>
                );

            case 'Work Completed':
                return (
                    <>
                        <Box sx={{ width: 'calc(100% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Work Completed On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {formatDate(deficiency.workCompletedOn, 'Not recorded')}
                            </Typography>
                        </Box>
                    </>
                );

            case 'Internally Signed Off':
                return (
                    <>
                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Signed Off On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {deficiency.internallySignedOffOn
                                    ? formatDate(deficiency.internallySignedOffOn)
                                    : 'Not recorded'}
                            </Typography>
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Signed Off By
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {getUserDisplayName(deficiency.internallySignedOffById)}
                            </Typography>
                        </Box>
                    </>
                );

            case 'Completed':
                return (
                    <>
                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Completed On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {formatDate(deficiency.deficiencyCompletedOn, 'Not recorded')}
                            </Typography>
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Signed Off By
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {getUserDisplayName(deficiency.completionSignOffById)}
                            </Typography>
                        </Box>
                    </>
                );

            case 'Rejected':
                return (
                    <>
                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Rejected By
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {getUserDisplayName(deficiency.rejectedById)}
                            </Typography>
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Rejected On
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {formatDate(deficiency.rejectedOn, 'Not recorded')}
                            </Typography>
                        </Box>

                        <Box sx={{ width: 'calc(50% - 8px)' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: '0.6875rem' }}
                            >
                                Stage Rejected
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', color: '#1E2026' }}
                            >
                                {getRejectedFrom()}
                            </Typography>
                        </Box>
                    </>
                );

            default:
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, fontSize: '0.75rem', color: '#1E2026' }}
            >
                {getSectionTitle()}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {renderStatusMetadata()}
            </Box>
        </Box>
    );
};

/**
 * Component for displaying and tracking deficiency status
 *
 * @param {Object} props - Component props
 * @param {Object} props.deficiency - The deficiency data object
 * @param {boolean} [props.isEditing=false] - Whether edit mode is active
 * @param {Function} [props.onChange] - Callback when a field is changed
 * @param {boolean} [props.readOnly=false] - Whether the component is in read-only mode
 * @returns {React.ReactElement} StatusTracker component
 */
const StatusTracker = ({ deficiency, isEditing = false, onChange, readOnly = false, role }) => {
    const { dispatch, state } = useDeficiencies();
    const { businesses, businessesLoading, users, usersLoading } = state;

    const useRoleMappedStatuses = role && !isInternalManagerRole(role);
    const trackerStatuses = useRoleMappedStatuses ? getTrackerStatuses(role) : STATUSES;

    const isRejected = deficiency.status === 'Rejected';

    const roleMappedDefOrder = useRoleMappedStatuses
        ? trackerStatuses.find(
              (s) =>
                  s.displayLabel ===
                  getStatusDisplay(
                      isRejected ? deficiency.rejectedFromStatus || 'Submitted' : deficiency.status,
                      role
                  ).displayLabel
          )?.statusOrder
        : undefined;

    const [selectedStatus, setSelectedStatus] = useState(
        STATUSES.find((s) => s.value === deficiency.status) || STATUSES[0]
    );
    const currentStatusIndex = STATUSES.findIndex((s) => s.value === deficiency.status);

    const selectedDisplayLabel = useRoleMappedStatuses
        ? getStatusDisplay(selectedStatus.value, role)?.displayLabel || selectedStatus.label
        : undefined;

    // Update selectedStatus when deficiency.status changes
    useEffect(() => {
        const newSelectedStatus =
            STATUSES.find((s) => s.value === deficiency.status) || STATUSES[0];
        setSelectedStatus(newSelectedStatus);
    }, [deficiency.status]);

    const handleStatusClick = (status) => {
        setSelectedStatus(status);
        dispatch({
            type: ACTIONS.OPEN_MODAL,
            payload: {
                type: 'status',
                deficiencyId: deficiency.deficiencyId,
                currentStatus: status.value,
            },
        });
    };

    // In edit mode, show the form fields without the status tracker visual
    if (isEditing) {
        return (
            <Box
                sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #F0F0F0',
                    overflow: 'hidden',
                }}
            >
                {/* Metadata without visual tracker */}
                <ConstantMetadata
                    deficiency={deficiency}
                    isEditing={isEditing}
                    onChange={onChange}
                    businesses={businesses}
                    businessesLoading={businessesLoading}
                    users={users}
                    usersLoading={usersLoading}
                    selectedStatus={selectedStatus}
                    readOnly={readOnly}
                    role={role}
                />

                <Divider />

                <VariableMetadata
                    deficiency={deficiency}
                    selectedStatus={selectedStatus}
                    isEditing={isEditing}
                    onChange={onChange}
                    businesses={businesses}
                    businessesLoading={businessesLoading}
                    users={users}
                    usersLoading={usersLoading}
                    readOnly={readOnly}
                />
            </Box>
        );
    }

    // In view mode, show the normal layout with status tracker
    return (
        <Box
            sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                border: '1px solid #F0F0F0',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex' }}>
                {/* Status Tracker */}
                <Box
                    sx={{
                        p: 1.5,
                        pr: 2,
                        borderRight: '1px solid #F0F0F0',
                        minWidth: '120px',
                        maxWidth: '120px',
                        display: { xs: 'none', sm: 'block' }, // Hide on mobile (xs), show on sm and up
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: '16px',
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                backgroundColor: '#E0E0E0',
                                zIndex: 0,
                            },
                        }}
                    >
                        {trackerStatuses.map((status, index) => {
                            const statusValue = status.value || status.displayLabel;
                            const statusLabel = status.displayLabel || status.label;

                            let isComplete = false;
                            let isCurrent = false;

                            if (useRoleMappedStatuses) {
                                if (isRejected) {
                                    isCurrent = statusLabel === 'Rejected';
                                    if (!isCurrent && roleMappedDefOrder != null) {
                                        isComplete = status.statusOrder <= roleMappedDefOrder;
                                    }
                                } else if (roleMappedDefOrder != null) {
                                    isComplete = status.statusOrder < roleMappedDefOrder;
                                    isCurrent = status.statusOrder === roleMappedDefOrder;
                                }
                            } else if (isRejected) {
                                const rejectedFromStatusDisplay = deficiency.rejectedFromStatus
                                    ? getDisplayStatusValue(deficiency.rejectedFromStatus)
                                    : 'Submitted';

                                const rejectedFromIndex = STATUSES.findIndex(
                                    (s) => s.value === rejectedFromStatusDisplay
                                );

                                const effectiveRejectedFromIndex =
                                    rejectedFromIndex !== -1 ? rejectedFromIndex : 0;

                                isComplete = index <= effectiveRejectedFromIndex;
                                isCurrent = statusValue === deficiency.status;
                            } else {
                                isComplete = index < currentStatusIndex;
                                isCurrent = index === currentStatusIndex;
                            }

                            const isSelected = useRoleMappedStatuses
                                ? statusLabel === selectedDisplayLabel
                                : statusValue === selectedStatus.value;
                            const isRejectedStatus = statusLabel === 'Rejected';

                            let iconColor;
                            if (isRejectedStatus && isCurrent) {
                                iconColor = theme.palette.status.error;
                            } else if (isComplete) {
                                iconColor = theme.palette.status.success;
                            } else if (isCurrent) {
                                iconColor =
                                    statusLabel === 'Completed'
                                        ? theme.palette.status.success
                                        : theme.palette.status.info;
                            } else {
                                iconColor = theme.palette.grey[400];
                            }

                            return (
                                <Box
                                    key={statusValue}
                                    onClick={() =>
                                        !useRoleMappedStatuses && handleStatusClick(status)
                                    }
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        cursor: useRoleMappedStatuses ? 'default' : 'pointer',
                                        position: 'relative',
                                        zIndex: 1,
                                        '&:hover': {
                                            '& .status-icon': {
                                                transform: useRoleMappedStatuses
                                                    ? 'none'
                                                    : 'scale(1.1)',
                                            },
                                        },
                                    }}
                                >
                                    {/* Status icon */}
                                    <Box
                                        className="status-icon"
                                        sx={{
                                            transition: 'transform 0.2s',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '50%',
                                            p: 0.2,
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: '2px',
                                        }}
                                    >
                                        {isComplete ? (
                                            <CheckCircleIcon
                                                sx={{
                                                    color: iconColor,
                                                    fontSize: '16px',
                                                }}
                                            />
                                        ) : isCurrent ? (
                                            <AdjustIcon
                                                sx={{
                                                    color: iconColor,
                                                    fontSize: '16px',
                                                }}
                                            />
                                        ) : (
                                            <RadioButtonUncheckedIcon
                                                sx={{
                                                    color: iconColor,
                                                    fontSize: '16px',
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Status label */}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected
                                                ? '#191C20'
                                                : isComplete
                                                  ? theme.palette.status.success
                                                  : isCurrent
                                                    ? isRejectedStatus
                                                        ? theme.palette.status.error
                                                        : statusLabel === 'Completed'
                                                          ? theme.palette.status.success
                                                          : theme.palette.status.info
                                                    : '#757575',
                                            fontSize: '0.75rem',
                                            whiteSpace: 'normal',
                                            lineHeight: 1.2,
                                            textAlign: 'left',
                                            width: '85px',
                                        }}
                                    >
                                        {statusLabel}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Metadata Sections */}
                <Box sx={{ flex: 1 }}>
                    {/* Constant Metadata */}
                    <ConstantMetadata
                        deficiency={deficiency}
                        isEditing={isEditing}
                        onChange={onChange}
                        businesses={businesses}
                        businessesLoading={businessesLoading}
                        users={users}
                        usersLoading={usersLoading}
                        selectedStatus={selectedStatus}
                        readOnly={readOnly}
                        role={role}
                    />

                    <Divider />

                    {/* Variable Metadata */}
                    <VariableMetadata
                        deficiency={deficiency}
                        selectedStatus={selectedStatus}
                        isEditing={isEditing}
                        onChange={onChange}
                        businesses={businesses}
                        businessesLoading={businessesLoading}
                        users={users}
                        usersLoading={usersLoading}
                        readOnly={readOnly}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default StatusTracker;
